import { z } from "zod";

/*
 * Invite-only access: who may sign up and sign in while Atrium is invite-only.
 * Access is tied to an email address, not to a username.
 */

export type AccessStatus = "requested" | "invited" | "accepted";

export interface AccessEntry {
  id: string;
  email: string;
  status: AccessStatus;
  note: string;
  createdAt: string;
  invitedAt: string | null;
  acceptedAt: string | null;
  /** Invite links expire; staff can send a fresh one. */
  inviteExpired: boolean;
  invitedBy: { username: string; displayName: string } | null;
  user: { id: string; username: string; displayName: string } | null;
}

export interface AccessListResponse {
  items: AccessEntry[];
  nextCursor: string | null;
  counts: Record<AccessStatus, number>;
}

export interface AccessInviteResult {
  email: string;
  outcome: "invited" | "reinvited" | "restored" | "already_has_access";
}

const email = z.string().trim().toLowerCase().email("Enter a valid email").max(254);

export const accessRequestSchema = z.object({ email });

export const staffInviteSchema = z.object({
  /** One address per line, or separated by commas or spaces. */
  emails: z
    .string()
    .max(20_000)
    .transform((s) => [...new Set(s.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))])
    .pipe(z.array(email).min(1, "Add at least one email address").max(100, "Invite at most 100 people at once")),
  note: z.string().trim().max(500).default(""),
});

export const staffAccessQuery = z.object({
  q: z.string().trim().max(100).default(""),
  status: z.enum(["all", "requested", "invited", "accepted"]).default("all"),
  cursor: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
